import {DisplayType} from "@data-force/app";
import {ArgsProps} from "antd/lib/message";
import {
    ADD_ALERTS,
    CLEAR_ALERTS,
    REMOVE_ALERT,
    SET_BULK_CONFIRMATION_DIALOG_ACTION,
    SET_CONTENT_TYPE,
    SET_DISPLAY_TYPE,
    SET_FILE_SELECTION_TYPE,
    SET_JOB_SECTION_INSTRUCTIONS_VISIBLE,
    SET_JOB_SECTION_STATUS_STATS_VISIBLE,
    SET_NAVIGATION_ELEMENTS,
    SET_NAVIGATION_SELECTION,
    SET_SIGNED_RESOURCES_MAP,
    SET_USER_MENU_OPEN_KEYS,
    SET_USER_MENU_VISIBLE,
    SHOW_RESET_TASK_DIALOG,
    SHOW_UPLOAD_OVERLAY,
    TOGGLE_SIDEBAR,
} from "../actions/availableActions";
import {LayoutState} from "../../interfaces/store/reducers/layoutState";
import {
    AddAlertsAction,
    ClearAlertsAction,
    RemoveAlertAction,
    SetBulkConfirmationDialogAction,
    SetContentTypeAction,
    SetDisplayTypeAction,
    SetFileSelectionTypeAction,
    SetJobSectionInstructionsVisible,
    SetNavigationElementsAction,
    SetNavigationSelectionAction,
    SetSignedResourcesMapAction,
    SetStatusStatsVisibleAction,
    SetUserMenuOpenKeysAction,
    SetUserMenuVisibleAction,
    ShowResetTaskDialogAction,
    ShowUploadOverlayAction,
    ToggleSidebarAction,
} from "../../interfaces/store/actions/layout";
import {ContentType} from "../../interfaces/store/actions/payload/layout";

const initialState: LayoutState = {
    header: {
        userMenu: {
            openKeys: [],
            visible: false,
        },
    },
    sidebar: {
        collapsed: true,
    },
    content: {
        contentType: ContentType.TASKS,
        fileSelectionType: null,
        isSinglePickerInitialized: false,
        navigation: {
            elements: [],
            selection: 0,
        },
        signedResourcesMap: {},
        alerts: [],
    },
    jobSection: {
        instructionsVisible: true,
        statusStatsVisible: true,
    },
    tasksSection: {
        displayType: DisplayType.INTAKER_WIZARD,
        showResetTaskDialog: false,
    },
    filePicker: {
        showOverlay: true,
    },
    footer: {
        dialog: {
            bulkConfirmationDialog: {
                event: "",
                visible: false,
            },
        },
    },
};

type AvailableActions = ToggleSidebarAction
| SetContentTypeAction
| SetFileSelectionTypeAction
| SetDisplayTypeAction
| SetNavigationElementsAction
| SetNavigationSelectionAction
| SetSignedResourcesMapAction
| ShowUploadOverlayAction
| ShowResetTaskDialogAction
| SetJobSectionInstructionsVisible
| SetBulkConfirmationDialogAction
| SetStatusStatsVisibleAction
| SetUserMenuOpenKeysAction
| SetUserMenuVisibleAction
| AddAlertsAction
| RemoveAlertAction
| ClearAlertsAction;

const layoutReducer = (state: LayoutState = initialState, action: AvailableActions): LayoutState => {
    switch (action.type) {
        case TOGGLE_SIDEBAR:
            return {
                ...state,
                sidebar: {
                    ...state.sidebar,
                    collapsed: !state.sidebar.collapsed,
                },
            };
        case SET_CONTENT_TYPE:
            return {
                ...state,
                content: {
                    ...state.content,
                    contentType: action.payload,
                },
            };
        case SET_FILE_SELECTION_TYPE:
            return {
                ...state,
                content: {
                    ...state.content,
                    fileSelectionType: action.payload,
                },
            };
        case SET_DISPLAY_TYPE:
            return {
                ...state,
                tasksSection: {
                    ...state.tasksSection,
                    displayType: action.payload,
                },
            };
        case SET_NAVIGATION_ELEMENTS:
            return {
                ...state,
                content: {
                    ...state.content,
                    navigation: {
                        ...state.content.navigation,
                        elements: action.payload,
                    },
                },
            };
        case SET_NAVIGATION_SELECTION:
            return {
                ...state,
                content: {
                    ...state.content,
                    navigation: {
                        ...state.content.navigation,
                        selection: action.payload,
                    },
                },
            };
        case SET_SIGNED_RESOURCES_MAP:
            return {
                ...state,
                content: {
                    ...state.content,
                    signedResourcesMap: {...action.payload},
                },
            };
        case SHOW_UPLOAD_OVERLAY:
            return {
                ...state,
                filePicker: {
                    showOverlay: action.payload,
                },
            };
        case SHOW_RESET_TASK_DIALOG:
            return {
                ...state,
                tasksSection: {
                    ...state.tasksSection,
                    showResetTaskDialog: action.payload,
                },
            };
        case SET_JOB_SECTION_INSTRUCTIONS_VISIBLE:
            return {
                ...state,
                jobSection: {
                    ...state.jobSection,
                    instructionsVisible: action.payload,
                },
            };
        case SET_BULK_CONFIRMATION_DIALOG_ACTION:
            return {
                ...state,
                footer: {
                    ...state.footer,
                    dialog: {
                        ...state.footer.dialog,
                        bulkConfirmationDialog: action.payload,
                    },
                },
            };
        case SET_JOB_SECTION_STATUS_STATS_VISIBLE:
            return {
                ...state,
                jobSection: {
                    ...state.jobSection,
                    statusStatsVisible: action.payload,
                },
            };
        case SET_USER_MENU_OPEN_KEYS:
            return {
                ...state,
                header: {
                    ...state.header,
                    userMenu: {
                        ...state.header.userMenu,
                        openKeys: action.payload,
                    },
                },
            };
        case SET_USER_MENU_VISIBLE:
            return {
                ...state,
                header: {
                    ...state.header,
                    userMenu: {
                        ...state.header.userMenu,
                        visible: action.payload,
                    },
                },
            };
        case ADD_ALERTS:
            return {
                ...state,
                content: {
                    ...state.content,
                    alerts: state.content.alerts.concat(action.payload),
                },
            };
        case REMOVE_ALERT: {
            const alerts: ArgsProps[] = state.content.alerts.slice();
            const index: number = alerts.findIndex((alert: ArgsProps): boolean => alert.key === action.payload);
            if (index >= 0) {
                alerts.splice(index, 1);
            }

            return {
                ...state,
                content: {
                    ...state.content,
                    alerts,
                },
            };
        }
        case CLEAR_ALERTS:
            return {
                ...state,
                content: {
                    ...state.content,
                    alerts: [],
                },
            };
        default:
            return state;
    }
};

export default layoutReducer;
